import { useEffect, useState, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Plus, Gift, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { MemberAvatar } from "@/components/MemberAvatar";
import { accentHex } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/shared";

export default function Rewards() {
  const { members, refreshMembers } = useMembers();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", cost: 20 });
  const [saving, setSaving] = useState(false);
  const [redeemFor, setRedeemFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, red] = await Promise.all([api("/rewards"), api("/rewards/redemptions")]);
      setRewards(r);
      setRedemptions(red);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the reward a name");
    setSaving(true);
    try {
      await api("/rewards", {
        method: "POST",
        body: { title: form.title.trim(), description: form.description.trim(), cost: Number(form.cost) || 10 },
      });
      setAddOpen(false);
      setForm({ title: "", description: "", cost: 20 });
      await load();
      toast.success("Reward added");
    } catch {
      toast.error("Could not save reward");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    setRewards((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await api(`/rewards/${r.id}`, { method: "DELETE" });
    } catch {
      load();
    }
  };

  const redeem = async (reward, member) => {
    try {
      await api("/rewards/redeem", { method: "POST", body: { reward_id: reward.id, member_id: member.id } });
      setRedeemFor(null);
      await Promise.all([load(), refreshMembers()]);
      toast.success(`${member.name} redeemed “${reward.title}”!`);
    } catch (e) {
      toast.error(e.body?.detail === "insufficient_points" ? "Not enough points yet" : "Could not redeem");
    }
  };

  return (
    <div>
      <PageHeader
        title="Rewards"
        subtitle="Turn points into real-life wins"
        action={
          <Button className="rounded-full" onClick={() => setAddOpen(true)} data-testid="add-reward-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Reward
          </Button>
        }
      />

      {/* Member points */}
      {members.length > 0 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
          {members.map((m) => (
            <div key={m.id} className="bento-tile flex min-w-[110px] flex-col items-center gap-1 p-4" data-testid={`reward-points-${m.id}`}>
              <MemberAvatar member={m} size="md" />
              <span className="text-sm font-semibold text-ink">{m.name}</span>
              <span className="font-display text-base font-bold" style={{ color: accentHex(m.color) }}>
                {m.points} pts
              </span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-4 space-y-3">
          {["s1", "s2"].map((k) => (
            <Skeleton key={k} className="h-20 w-full rounded-3xl" />
          ))}
        </div>
      ) : rewards.length === 0 ? (
        <EmptyState
          emoji="🎁"
          title="No rewards yet"
          message="Create rewards your family can redeem with earned points."
          action={
            <Button className="rounded-full" onClick={() => setAddOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Add a reward
            </Button>
          }
        />
      ) : (
        <div className="mt-4 space-y-3">
          {rewards.map((r) => (
            <div key={r.id} className="bento-tile flex items-center gap-4 p-4" data-testid={`reward-${r.id}`}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary text-ink">
                <Gift className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-semibold text-ink">{r.title}</p>
                {r.description && <p className="truncate text-sm text-muted-foreground">{r.description}</p>}
                <p className="mt-0.5 text-sm font-semibold text-clay">{r.cost} points</p>
              </div>
              <Button className="rounded-full" onClick={() => setRedeemFor(r)} data-testid={`redeem-${r.id}`}>
                Redeem
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-muted-foreground"
                onClick={() => remove(r)}
                data-testid={`delete-reward-${r.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Redemption history */}
      {redemptions.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 flex items-center gap-1.5 px-1 font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <Clock className="h-4 w-4" /> Recent redemptions
          </h2>
          <div className="bento-tile divide-y divide-border/70 p-1">
            {redemptions.slice(0, 25).map((red, i) => {
              const mem = members.find((m) => m.id === red.member_id);
              return (
                <div key={red.id || i} className="flex items-center gap-3 px-3 py-2.5" data-testid={`redemption-${red.id || i}`}>
                  {mem ? <MemberAvatar member={mem} size="sm" /> : <Gift className="h-6 w-6 text-muted-foreground" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {mem?.name || "Someone"} redeemed “{red.reward_title}”
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {red.cost} pts · {red.redeemed_at ? formatDistanceToNow(new Date(red.redeemed_at), { addSuffix: true }) : ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add reward dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">New reward</DialogTitle>
            <DialogDescription className="sr-only">Reward name, description and point cost</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Movie night"
                className="rounded-xl"
                data-testid="reward-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Pick the film and the snacks"
                className="rounded-xl"
                data-testid="reward-desc-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cost (points)</Label>
              <Input
                type="number"
                min={1}
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className="rounded-xl"
                data-testid="reward-cost-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="reward-save-btn">
              {saving ? "Saving…" : "Save reward"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem member picker */}
      <Dialog open={!!redeemFor} onOpenChange={(o) => !o && setRedeemFor(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Who&apos;s redeeming?</DialogTitle>
            <DialogDescription>
              {redeemFor?.title} · {redeemFor?.cost} pts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {members.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">Add family members first.</p>
            )}
            {members.map((m) => {
              const enough = m.points >= (redeemFor?.cost || 0);
              return (
                <button
                  key={m.id}
                  disabled={!enough}
                  onClick={() => redeemFor && redeem(redeemFor, m)}
                  data-testid={`redeem-member-${m.id}`}
                  className="flex w-full items-center gap-3 rounded-xl bg-secondary/60 px-3 py-2.5 text-left transition-opacity disabled:opacity-40"
                >
                  <MemberAvatar member={m} size="sm" />
                  <span className="flex-1 font-semibold text-ink">{m.name}</span>
                  <span className={`text-sm font-semibold ${enough ? "text-mint" : "text-destructive"}`}>{m.points} pts</span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
